<template>
<div>
    <Breadcrumbs main="Admin" title="Skilled Professional Review" />
    <div class="container-fluid">
        <div v-if="loading && !profile.id" class="text-center my-5">
            <b-spinner variant="primary"></b-spinner>
        </div>
        <div v-else-if="error && !profile.id" class="text-center my-5 text-danger">
            Could not load this profile.
        </div>

        <div v-else>
            <!-- Header -->
            <div class="card">
                <div class="card-body">
                    <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center" style="gap: 16px;">
                        <div>
                            <h5 class="mb-1">{{ fullName || 'N/A' }}</h5>
                            <div style="color: gray;">
                                {{ profile.user?.email }}
                                <span v-if="profile.organisation"> ·
                                    <router-link :to="`/admin/sp-organisation-view/${profile.organisation.id}`">{{ profile.organisation.name }}</router-link>
                                </span>
                            </div>
                            <div class="d-flex flex-wrap mt-2" style="gap: 4px;">
                                <b-badge v-for="role in profile.role_categories" :key="role" variant="info">{{ roleLabel(role) }}</b-badge>
                            </div>
                        </div>
                        <div class="d-flex flex-column align-items-md-end" style="gap: 10px;">
                            <b-badge :variant="statusVariant(profile.verification_status)" style="text-transform: capitalize; font-size: 14px;">● {{ profile.verification_status }}</b-badge>
                            <div v-if="profile.verification_status === 'submitted'" class="d-flex" style="gap: 8px;">
                                <b-button variant="outline-danger" :disabled="submitting" v-b-modal.modal-reject>Reject…</b-button>
                                <b-button variant="success" :disabled="submitting" @click="confirmVerify">Verify ✓</b-button>
                            </div>
                        </div>
                    </div>

                    <div class="row mt-3" style="font-size: 13px; color: gray;">
                        <div class="col-sm-4">Submitted: <span class="text-dark">{{ formatDate(profile.submitted_at) }}</span></div>
                        <div class="col-sm-4" v-if="profile.verified_at">Verified: <span class="text-dark">{{ formatDate(profile.verified_at) }}</span></div>
                    </div>

                    <b-alert v-if="profile.review_note" show variant="danger" class="mt-3 mb-0">
                        <strong>Previous review note:</strong> {{ profile.review_note }}
                    </b-alert>
                    <b-alert v-if="profile.missing_requirements && profile.missing_requirements.length" show variant="warning" class="mt-3 mb-0">
                        <strong>Missing requirements:</strong>
                        {{ profile.missing_requirements.map(requirementLabel).join(', ') }}
                    </b-alert>
                    <b-alert v-if="hasConflict" show variant="warning" class="mt-3 mb-0">
                        <strong>⚠ Conflict of interest declared:</strong> "{{ profile.conflict_of_interest_disclosure }}"
                    </b-alert>
                </div>
            </div>

            <div class="row">
                <!-- About -->
                <div class="col-md-5">
                    <div class="card">
                        <div class="card-body">
                            <h6 class="mb-3">About</h6>
                            <div class="mb-2">
                                <h7 style="color: gray;">Headline</h7>
                                <h6 style="margin-top: 2px;">{{ profile.headline || 'N/A' }}</h6>
                            </div>
                            <div class="mb-2">
                                <h7 style="color: gray;">Years of Experience</h7>
                                <h6 style="margin-top: 2px;">{{ profile.years_of_experience != null ? profile.years_of_experience : 'N/A' }}</h6>
                            </div>
                            <div class="mb-2">
                                <h7 style="color: gray;">Country</h7>
                                <h6 style="margin-top: 2px;">{{ profile.country || 'N/A' }}</h6>
                            </div>
                            <div class="mb-2">
                                <h7 style="color: gray;">Expertise Areas</h7>
                                <div v-if="profile.expertise_areas && profile.expertise_areas.length" class="d-flex flex-wrap" style="gap: 4px; margin-top: 4px;">
                                    <b-badge v-for="area in profile.expertise_areas" :key="area" variant="info" style="text-transform: capitalize;">{{ area }}</b-badge>
                                </div>
                                <h6 v-else>N/A</h6>
                            </div>
                            <div>
                                <h7 style="color: gray;">Bio</h7>
                                <div v-if="profile.bio" style="margin-top: 4px; white-space: pre-line;">{{ profile.bio }}</div>
                                <h6 v-else>N/A</h6>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="col-md-7">
                    <!-- 1. Identity & KYC -->
                    <div class="card">
                        <div class="card-body">
                            <h6 class="mb-3">1. Identity &amp; KYC</h6>
                            <document-list :documents="documentsFor('identity')" />
                            <div class="mt-2">
                                <h7 style="color: gray;">Tax ID</h7>
                                <h6 style="margin-top: 2px;">
                                    <span v-if="profile.tax_id_number">{{ profile.tax_id_type }} {{ profile.tax_id_number }}</span>
                                    <span v-else>N/A</span>
                                </h6>
                            </div>
                        </div>
                    </div>

                    <!-- 2. Credentials -->
                    <div class="card">
                        <div class="card-body">
                            <h6 class="mb-3">2. Credentials</h6>
                            <document-list :documents="documentsFor('credentials')" />
                            <div class="mt-2">
                                <h7 style="color: gray;">LinkedIn</h7>
                                <h6 style="margin-top: 2px;">
                                    <a v-if="profile.linkedin_url" :href="profile.linkedin_url" target="_blank" rel="noopener noreferrer">{{ profile.linkedin_url }} ↗</a>
                                    <span v-else>N/A</span>
                                </h6>
                            </div>
                        </div>
                    </div>

                    <!-- 3. Certifications -->
                    <div class="card">
                        <div class="card-body">
                            <h6 class="mb-3">3. Certifications</h6>
                            <document-list :documents="documentsFor('certifications')" />
                        </div>
                    </div>

                    <!-- 4. Track record -->
                    <div class="card">
                        <div class="card-body">
                            <h6 class="mb-3">4. Track Record</h6>
                            <document-list :documents="documentsFor('track_record')" />
                            <div class="mt-3">
                                <h7 style="color: gray;">References</h7>
                                <div v-if="profile.references && profile.references.length" class="d-flex flex-wrap" style="gap: 16px; margin-top: 4px;">
                                    <div v-for="(ref, i) in profile.references" :key="i" class="p-2" style="border: 1px solid lightgray; border-radius: 8px; min-width: 220px;">
                                        <div><strong>{{ ref.name }}</strong></div>
                                        <div v-if="ref.designation || ref.organisation" style="color: gray;">{{ [ref.designation, ref.organisation].filter(Boolean).join(', ') }}</div>
                                        <div v-if="ref.email"><a :href="`mailto:${ref.email}`">{{ ref.email }}</a></div>
                                        <div v-if="ref.phone">{{ ref.phone }}</div>
                                    </div>
                                </div>
                                <h6 v-else>N/A</h6>
                            </div>
                        </div>
                    </div>

                    <!-- 5. Agreements -->
                    <div class="card">
                        <div class="card-body">
                            <h6 class="mb-3">5. Agreements</h6>
                            <div class="d-flex flex-wrap mb-2" style="gap: 8px;">
                                <b-badge :variant="profile.code_of_conduct_agreed ? 'success' : 'danger'">Code of Conduct / PSEA {{ profile.code_of_conduct_agreed ? '✓' : '✗' }}</b-badge>
                                <b-badge :variant="profile.nda_agreed ? 'success' : 'danger'">NDA {{ profile.nda_agreed ? '✓' : '✗' }}</b-badge>
                                <b-badge :variant="profile.conflict_of_interest_agreed ? 'success' : 'danger'">Conflict of Interest {{ profile.conflict_of_interest_agreed ? '✓' : '✗' }}</b-badge>
                            </div>
                            <div v-if="profile.signed_name">
                                Signed "<strong>{{ profile.signed_name }}</strong>" on {{ formatDate(profile.agreed_at) }}
                            </div>
                            <div v-else style="color: gray;">Not signed yet</div>
                            <div v-if="hasConflict" class="mt-2 p-2" style="background: #fff3cd; border-radius: 6px; color: #856404;">
                                ⚠ Conflict declared: "{{ profile.conflict_of_interest_disclosure }}"
                            </div>
                        </div>
                    </div>

                    <!-- Uncategorised documents, so nothing uploaded is hidden from the reviewer -->
                    <div v-if="otherDocuments.length" class="card">
                        <div class="card-body">
                            <h6 class="mb-3">Other Documents</h6>
                            <document-list :documents="otherDocuments" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Reject Modal -->
    <b-modal id="modal-reject" centered title="Reject profile" ok-title="Reject" ok-variant="danger" :ok-disabled="!reviewNote.trim() || submitting" @ok="handleReject" @hidden="reviewNote = ''">
        <p style="color: gray;">The professional will see this note on their form and can fix the profile and submit again.</p>
        <b-form-textarea v-model="reviewNote" placeholder="e.g. Address proof is not in English. Upload a certified translation." rows="4"></b-form-textarea>
    </b-modal>
</div>
</template>

<script>
import { mapState } from 'vuex';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import { ROLE_LABELS, SECTION_DOCUMENTS, REQUIREMENT_LABELS, statusVariant, formatDate } from './skilled-professional-helpers';
import DocumentList from './sp-document-list.vue';


export default {
    components: { DocumentList },

    data() {
        return {
            reviewNote: '',
            submitting: false
        };
    },

    computed: {
        ...mapState({
            detail: state => state.skilledProfessional.professionalDetail,
            loading: state => state.skilledProfessional.professionalLoading,
            error: state => state.skilledProfessional.professionalError
        }),

        // Ignore a stale profile left in the store from a previous visit
        profile() {
            return this.detail && String(this.detail.id) === String(this.$route.params.id) ? this.detail : {};
        },

        fullName() {
            return [this.profile.user?.first_name, this.profile.user?.last_name].filter(Boolean).join(' ');
        },

        hasConflict() {
            const text = this.profile.conflict_of_interest_disclosure;
            return !!(text && text.trim());
        },

        otherDocuments() {
            const known = Object.values(SECTION_DOCUMENTS).flat();
            return (this.profile.documents || []).filter(doc => !known.includes(doc.document_type));
        }
    },

    watch: {
        '$route.params.id': {
            handler(id) {
                if (id) this.$store.dispatch('skilledProfessional/fetchProfessionalDetail', id);
            },
            immediate: true
        }
    },

    methods: {
        documentsFor(section) {
            const types = SECTION_DOCUMENTS[section];
            return (this.profile.documents || [])
                .filter(doc => types.includes(doc.document_type))
                .sort((a, b) => types.indexOf(a.document_type) - types.indexOf(b.document_type));
        },

        roleLabel(role) {
            return ROLE_LABELS[role] ? ROLE_LABELS[role].long : role;
        },

        requirementLabel(key) {
            return REQUIREMENT_LABELS[key] || key;
        },

        confirmVerify() {
            Swal.fire({
                title: 'Verify this professional?',
                text: 'A feed post will be created and the user will be notified.',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Verify'
            }).then(result => {
                if (result.isConfirmed || result.value) {
                    this.submitVerification({ verification_status: 'verified' });
                }
            });
        },

        handleReject(event) {
            if (!this.reviewNote.trim()) {
                event.preventDefault();
                return;
            }
            this.submitVerification({ verification_status: 'rejected', review_note: this.reviewNote.trim() });
        },

        submitVerification(body) {
            this.submitting = true;
            this.$store.dispatch('skilledProfessional/updateVerification', {
                id: this.profile.id,
                ...body
            }).then(response => {
                Swal.fire({
                    title: response.code == 200 ? 'Success!' : 'Error!',
                    text: response.response,
                    icon: response.code == 200 ? 'success' : 'error'
                });
            }).finally(() => {
                this.submitting = false;
            });
        },

        statusVariant,
        formatDate
    }
};
</script>
